import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createCarrier,
    getCarrierById,
    updateCarrier,
    getUsersCarriersNotAssigned,
    getCarriers,
    logError
} from '../api';
import { Button, Form } from 'react-bootstrap';
import EditCancelModal from './Global/EditCancelModal';

const CarrierForm = ({ embedded = false, onSave, onCancel }) => {
    const [carrier, setCarrier] = useState({
        firstName: '',
        lastName1: '',
        lastName2: '',
        phone: null,
        email: null,
        colourHex: '#000000',
        userId: null,
        user: null,
        managerId: null,
        manager: null,
        subordinates: [],
        updatedSubordinatesIds: [],
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [usersNotAssigned, setUsersNotAssigned] = useState([]);
    const [isUserAssigned, setIsUserAssigned] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [allCarriers, setAllCarriers] = useState([]);
    const [availableSubordinates, setAvailableSubordinates] = useState([]);
    const [isCarrierManager, setIsCarrierManager] = useState(false);

    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (!embedded) {
            if (id) {
                setIsEditing(true);
                getCarrierById(id)
                    .then(async data => {
                        setCarrier(data);
                        if (data.user?.id) {
                            setIsUserAssigned(true);
                        } else {
                            await fetchUsersNotAssigned();
                        }
                        await fetchAllCarriers(data);
                        setIsCarrierManager(data.user?.roles?.some(r => r.id === 7)); // ID 7 = Jefe de Transportistas
                    })
                    .catch(logError);
            } else {
                fetchUsersNotAssigned();
            }
        }
    }, [id, embedded]);

    const fetchAllCarriers = async (current) => {
        try {
            const all = await getCarriers();
            const filtered = all.filter(sr => sr.id !== current.id);
            setAllCarriers(all);
            setAvailableSubordinates(filtered);
        } catch (err) {
            logError(err);
        }
    };

    const fetchUsersNotAssigned = async () => {
        try {
            setLoadingUsers(true);
            const users = await getUsersCarriersNotAssigned();
            setUsersNotAssigned(users);
        } catch (error) {
            logError(error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCarrier(prev => ({ ...prev, [name]: value }));
        setIsDirty(true);
    };

    const handleColorChange = (e) => {
        setCarrier(prev => ({ ...prev, colourHex: e.target.value }));
        setIsDirty(true);
    };

    const handleUserSelect = (e) => {
        const selectedUser = usersNotAssigned.find(user => user.id === parseInt(e.target.value));
        setCarrier(prev => ({
            ...prev,
            userId: selectedUser.id,
            user: selectedUser
        }));
        setIsUserAssigned(true);
        setIsDirty(true);
    };

    const handleRemoveAssignment = () => {
        const previousUser = carrier.user;
        setCarrier(prev => ({ ...prev, userId: null, user: null }));
        setIsUserAssigned(false);

        getUsersCarriersNotAssigned()
            .then(users => setUsersNotAssigned([previousUser, ...users]))
            .catch(logError);
        setIsDirty(true);
    };

    const handleSubordinateToggle = (subId) => {
        const exists = carrier.subordinates?.some(s => s.id === subId);
        const updated = exists
            ? carrier.subordinates.filter(s => s.id !== subId)
            : [...(carrier.subordinates || []), allCarriers.find(s => s.id === subId)];
        setCarrier(prev => ({ ...prev, subordinates: updated }));
        setIsDirty(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const carrierToSubmit = {
            ...carrier,
            user: undefined,
            manager: undefined,
            updatedSubordinatesIds: carrier.subordinates?.map(sub => sub.id) || [],
            subordinates: []
        };

        if (isEditing) {
            updateCarrier(id, carrierToSubmit)
                .then(() => navigate('/carriers'))
                .catch(err => logError(err));
        } else {
            createCarrier(carrierToSubmit)
                .then(() => navigate('/carriers'))
                .catch(err => logError(err));
        }
    };

    const handleSaveEmbedded = () => {
        if (onSave) {
            onSave(carrier);
        }
    };

    const handleCancel = () => {
        if (isDirty) setShowCancelModal(true);
        else navigate('/carriers');
    };

    return (
        <div>
            {!embedded && <h2>{isEditing ? 'Editar Transportista' : 'Agregar Nuevo Transportista'}</h2>}

            <Form onSubmit={embedded ? (e) => e.preventDefault() : handleSubmit}>
                <Form.Group><Form.Label>Nombre *</Form.Label>
                    <Form.Control type="text" name="firstName" value={carrier.firstName} onChange={handleChange} required data-testid="carrier-name-input" />
                </Form.Group>

                <Form.Group><Form.Label>Primer Apellido</Form.Label>
                    <Form.Control type="text" name="lastName1" value={carrier.lastName1} onChange={handleChange} required data-testid="carrier-lastname1-input" />
                </Form.Group>

                <Form.Group><Form.Label>Segundo Apellido</Form.Label>
                    <Form.Control type="text" name="lastName2" value={carrier.lastName2} onChange={handleChange} data-testid="carrier-lastname2-input" />
                </Form.Group>

                <Form.Group><Form.Label>Email *</Form.Label>
                    <Form.Control type="email" name="email" value={carrier.email} onChange={handleChange} required data-testid="carrier-email-input" />
                </Form.Group>

                <Form.Group><Form.Label>Teléfono *</Form.Label>
                    <Form.Control type="text" name="phone" value={carrier.phone} onChange={handleChange} required data-testid="carrier-phone-input" />
                </Form.Group>

                <Form.Group><Form.Label>Color</Form.Label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: 25, height: 25, backgroundColor: carrier.colourHex, border: '1px solid #000', borderRadius: '50%' }} />
                        <Form.Control type="color" name="colourHex" value={carrier.colourHex} onChange={handleColorChange} data-testid="carrier-color-input" />
                    </div>
                </Form.Group>

                {isUserAssigned ? (
                    <Form.Group>
                        <Form.Label>Usuario Asignado</Form.Label>
                        <div className="d-flex justify-content-between">
                            <span>{carrier.user?.username}</span>
                            <Button variant="danger" onClick={handleRemoveAssignment}>Quitar Asignación</Button>
                        </div>
                    </Form.Group>
                ) : (
                    !embedded && (
                        <Form.Group>
                            <Form.Label>Asignar Usuario</Form.Label>
                            <Form.Control as="select" name="userId" value={carrier.userId || ''} onChange={handleUserSelect}>
                                <option value="">Seleccionar Usuario</option>
                                {loadingUsers
                                    ? <option value="">Cargando...</option>
                                    : usersNotAssigned.map(user => (
                                        <option key={user.id} value={user.id}>{user.username}</option>
                                    ))}
                            </Form.Control>
                        </Form.Group>
                    )
                )}

                {isCarrierManager && (
                    <Form.Group>
                        <Form.Label>Subordinados</Form.Label>
                        {availableSubordinates.map(sub => (
                            <Form.Check
                                key={sub.id}
                                type="checkbox"
                                label={`${sub.firstName} ${sub.lastName1}`}
                                checked={carrier.subordinates?.some(s => s.id === sub.id)}
                                onChange={() => handleSubordinateToggle(sub.id)}
                            />
                        ))}
                    </Form.Group>
                )}

                {carrier.manager && (
                    <Form.Group className="mt-3">
                        <Form.Label>Jefe actual</Form.Label>
                        <div>{carrier.manager.firstName} {carrier.manager.lastName1}</div>
                    </Form.Group>
                )}

                <div className="d-flex justify-content-end mt-4">
                    {embedded ? (
                        <div className="d-flex justify-content-between w-100">
                            <Button variant="primary" onClick={handleSaveEmbedded} data-testid="carrier-save-button">Guardar</Button>
                            <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
                        </div>
                    ) : (
                        <div className="d-flex justify-content-between w-100">
                            <Button variant="primary" type="submit" data-testid="carrier-submit-button">{isEditing ? 'Guardar Cambios' : 'Crear Transportista'}</Button>
                            <Button variant="secondary" onClick={handleCancel} data-testid="carrier-cancel-button">Cancelar</Button>
                        </div>
                    )}
                </div>
            </Form>

            {!embedded && (
                <EditCancelModal
                    show={showCancelModal}
                    onConfirm={() => navigate('/carriers')}
                    onCancel={() => setShowCancelModal(false)}
                />
            )}
        </div>
    );
};

export default CarrierForm;
